import React, { useState } from "react";
import {
  Button,
  Form,
  Input,
  notification,
  Space,
  Typography,
  Row,
  Col,
  Card,
  Divider,
  Tag,
  Avatar,
} from "antd";
import {
  MailOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  WhatsAppOutlined,
  ClockCircleOutlined,
  MessageOutlined,
  CheckCircleOutlined,
  UserOutlined,
} from "@ant-design/icons";

const { Title, Paragraph, Text } = Typography;

const Contact = () => {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  const onFinish = (values) => {
    setLoading(true);
    
    // Format the message for WhatsApp
    const message = `*NEW CONTACT MESSAGE - DOLLAR FURNITURE*%0A%0A` +
                    `👤 *Name:* ${values.name}%0A` +
                    `📧 *Email:* ${values.email}%0A` +
                    `📱 *Phone:* ${values.phone || 'Not Provided'}%0A` +
                    `💬 *Message:* ${values.message}%0A%0A` +
                    `🕒 *Submitted:* ${new Date().toLocaleString()}%0A` +
                    `🌐 *Via:* Website Contact Form`;
    
    // WhatsApp API URL
    const whatsappUrl = `https://wa.me/254729335354?text=${message}`;
    
    // Open WhatsApp in new tab
    window.open(whatsappUrl, '_blank');
    
    // Show success notification
    notification.success({
      message: "✅ Message Ready for WhatsApp",
      description: "Your message has been prepared. Please send it in WhatsApp to contact us directly!",
      duration: 5,
    });
    
    // Reset form
    form.resetFields();
    setLoading(false);
  };

  return (
    <div
      style={{
        padding: "40px 20px",
        background: "linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)",
        minHeight: "100vh",
        color: "#ffffff",
      }}
    >
      {/* Hero Header */}
      <div style={{ textAlign: "center", marginBottom: "60px" }}>
        <div style={{ marginBottom: "30px" }}>
          <Tag 
            color="gold" 
            style={{ 
              fontSize: "14px", 
              padding: "8px 20px",
              borderRadius: "20px",
              fontWeight: 600,
            }}
          >
            24/7 Customer Support
          </Tag>
        </div>
        
        <Title 
          level={1} 
          style={{ 
            color: "#ffffff", 
            fontWeight: 800,
            fontSize: "48px",
            marginBottom: "20px",
            background: "linear-gradient(135deg, #ffa500, #ff6b35)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          We're Here To Help You
        </Title>
        
        <Paragraph
          style={{
            color: "#bbb",
            fontSize: "18px",
            maxWidth: "700px",
            margin: "0 auto",
            lineHeight: 1.6,
          }}
        >
          Have questions about our furniture collections? Need design advice? 
          Our team is available 24/7 to assist you with any inquiries.
        </Paragraph>
      </div>

      <Row gutter={[32, 32]} style={{ marginBottom: "60px" }}>
        {/* Stats Section */}
        <Col xs={24}>
          <Row gutter={[16, 16]} justify="center">
            {[
              { label: "24/7 Support", value: "Always Open", icon: "⏰", color: "#52c41a" },
              { label: "Response Time", value: "< 5 mins", icon: "⚡", color: "#1890ff" },
              { label: "Customer Rating", value: "4.9/5", icon: "⭐", color: "#ffc107" },
              { label: "Years Experience", value: "10+", icon: "🏆", color: "#722ed1" },
            ].map((stat, index) => (
              <Col xs={12} sm={6} key={index}>
                <Card
                  bordered={false}
                  style={{
                    borderRadius: "15px",
                    background: "linear-gradient(135deg, #1a1a1a, #222)",
                    border: "1px solid #333",
                    textAlign: "center",
                    height: "100%",
                  }}
                  bodyStyle={{ padding: "20px" }}
                >
                  <div style={{ 
                    fontSize: "36px", 
                    marginBottom: "10px",
                    color: stat.color,
                  }}>
                    {stat.icon}
                  </div>
                  <Text strong style={{ 
                    fontSize: "28px", 
                    color: "#ffffff",
                    display: "block",
                    marginBottom: "5px",
                  }}>
                    {stat.value}
                  </Text>
                  <Text style={{ color: "#aaa", fontSize: "14px" }}>
                    {stat.label}
                  </Text>
                </Card>
              </Col>
            ))}
          </Row>
        </Col>
      </Row>

      <Row gutter={[32, 32]}>
        {/* Left Column: Contact Form */}
        <Col xs={24} lg={12}>
          <Card
            bordered={false}
            style={{
              borderRadius: "20px",
              background: "linear-gradient(135deg, #1a1a1a, #222)",
              border: "1px solid #333",
              boxShadow: "0 20px 40px rgba(0, 0, 0, 0.3)",
            }}
          >
            <div style={{ marginBottom: "30px" }}>
              <Title level={3} style={{ color: "#ffffff", marginBottom: "10px" }}>
                <MessageOutlined style={{ marginRight: "10px", color: "#1890ff" }} />
                Send Your Message
              </Title>
              <Paragraph style={{ color: "#bbb" }}>
                Fill the form below and we'll connect you directly via WhatsApp
              </Paragraph>
            </div>
            
            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              size="large"
            >
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label={<Text style={{ color: "#ddd", fontWeight: 500 }}>Full Name</Text>}
                    name="name"
                    rules={[{ required: true, message: "Please enter your name!" }]}
                  >
                    <Input 
                      placeholder="Your name" 
                      prefix={<UserOutlined style={{ color: "#666" }} />}
                      style={{
                        backgroundColor: "#0a0a0a",
                        borderColor: "#444",
                        color: "#ffffff",
                        borderRadius: "10px",
                        padding: "12px",
                      }}
                    />
                  </Form.Item>
                </Col>
                
                <Col xs={24} md={12}>
                  <Form.Item
                    label={<Text style={{ color: "#ddd", fontWeight: 500 }}>Phone Number</Text>}
                    name="phone"
                  >
                    <Input 
                      placeholder="Optional phone number"
                      prefix={<PhoneOutlined style={{ color: "#666" }} />}
                      style={{
                        backgroundColor: "#0a0a0a",
                        borderColor: "#444",
                        color: "#ffffff",
                        borderRadius: "10px",
                        padding: "12px",
                      }}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                label={<Text style={{ color: "#ddd", fontWeight: 500 }}>Email Address</Text>}
                name="email"
                rules={[
                  { required: true, message: "Please enter your email!" },
                  { type: "email", message: "Please enter a valid email!" },
                ]}
              >
                <Input 
                  placeholder="Your email address" 
                  prefix={<MailOutlined style={{ color: "#666" }} />}
                  style={{
                    backgroundColor: "#0a0a0a",
                    borderColor: "#444",
                    color: "#ffffff",
                    borderRadius: "10px",
                    padding: "12px",
                  }}
                />
              </Form.Item>

              <Form.Item
                label={<Text style={{ color: "#ddd", fontWeight: 500 }}>Your Message</Text>}
                name="message"
                rules={[{ required: true, message: "Please enter your message!" }]}
              >
                <Input.TextArea 
                  placeholder="Tell us what you need help with..."
                  rows={5}
                  style={{
                    backgroundColor: "#0a0a0a",
                    borderColor: "#444",
                    color: "#ffffff",
                    borderRadius: "10px",
                    padding: "12px",
                    resize: "vertical",
                  }}
                />
              </Form.Item>

              <Button
                type="primary"
                htmlType="submit"
                block
                loading={loading}
                icon={<WhatsAppOutlined />}
                style={{
                  height: "56px",
                  fontSize: "18px",
                  fontWeight: 600,
                  borderRadius: "12px",
                  background: "linear-gradient(135deg, #25D366 0%, #128C7E 100%)",
                  border: "none",
                  marginTop: "10px",
                }}
              >
                Send via WhatsApp
              </Button>
            </Form>
            
            <Divider style={{ borderColor: "#333", margin: "30px 0" }} />
            
            <div style={{ textAlign: "center" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "10px", marginBottom: "10px" }}>
                <CheckCircleOutlined style={{ color: "#52c41a", fontSize: "18px" }} />
                <Text style={{ color: "#52c41a", fontSize: "14px" }}>
                  Instant WhatsApp Connection
                </Text>
              </div>
              <Text type="secondary" style={{ fontSize: "14px" }}>
                Your message opens directly in WhatsApp for immediate response
              </Text>
            </div>
          </Card>
        </Col>

        {/* Right Column: Contact Info & Map */}
        <Col xs={24} lg={12}>
          <Row gutter={[24, 24]} style={{ height: "100%" }}>
            {/* Contact Info Card */}
            <Col xs={24}>
              <Card
                bordered={false}
                style={{
                  borderRadius: "20px",
                  background: "linear-gradient(135deg, #1a1a1a, #222)",
                  border: "1px solid #333",
                  height: "100%",
                }}
              >
                <Title level={3} style={{ color: "#ffffff", marginBottom: "30px" }}>
                  <PhoneOutlined style={{ marginRight: "10px", color: "#1890ff" }} />
                  Direct Contact Channels
                </Title>
                
                <Space direction="vertical" size="large" style={{ width: "100%" }}>
                  {/* WhatsApp */}
                  <div style={{ 
                    display: "flex", 
                    alignItems: "center", 
                    gap: "15px",
                    padding: "20px",
                    backgroundColor: "#0a0a0a",
                    borderRadius: "15px",
                    border: "1px solid #333",
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.borderColor = "#25D366"}
                  onMouseLeave={(e) => e.currentTarget.style.borderColor = "#333"}
                  onClick={() => window.open("https://wa.me/254729335354", "_blank")}
                  >
                    <div style={{
                      background: "linear-gradient(135deg, #25D366, #128C7E)",
                      borderRadius: "12px",
                      width: "56px",
                      height: "56px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}>
                      <WhatsAppOutlined style={{ fontSize: "24px", color: "white" }} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <Text strong style={{ fontSize: "16px", color: "#ffffff", display: "block" }}>
                        24/7 WhatsApp Support
                      </Text>
                      <Text style={{ color: "#bbb", fontSize: "14px", marginTop: "5px" }}>
                        Instant response - Click to chat directly
                      </Text>
                      <Text strong style={{ color: "#25D366", fontSize: "16px", marginTop: "10px", display: "block" }}>
                        +254 729 335 354
                      </Text>
                    </div>
                  </div>

                  {/* Phone */}
                  <div style={{ 
                    display: "flex", 
                    alignItems: "center", 
                    gap: "15px",
                    padding: "20px",
                    backgroundColor: "#0a0a0a",
                    borderRadius: "15px",
                    border: "1px solid #333",
                  }}>
                    <div style={{
                      background: "linear-gradient(135deg, #1890ff, #0050b3)",
                      borderRadius: "12px",
                      width: "56px",
                      height: "56px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}>
                      <PhoneOutlined style={{ fontSize: "24px", color: "white" }} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <Text strong style={{ fontSize: "16px", color: "#ffffff", display: "block" }}>
                        Phone Support
                      </Text>
                      <Text style={{ color: "#bbb", fontSize: "14px", marginTop: "5px" }}>
                        Call us anytime for assistance
                      </Text>
                      <Text strong style={{ color: "#1890ff", fontSize: "16px", marginTop: "10px", display: "block" }}>
                        +254 729 335 354
                      </Text>
                    </div>
                  </div>

                  {/* Email */}
                  <div style={{ 
                    display: "flex", 
                    alignItems: "center", 
                    gap: "15px",
                    padding: "20px",
                    backgroundColor: "#0a0a0a",
                    borderRadius: "15px",
                    border: "1px solid #333",
                  }}>
                    <div style={{
                      background: "linear-gradient(135deg, #722ed1, #391085)",
                      borderRadius: "12px",
                      width: "56px",
                      height: "56px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}>
                      <MailOutlined style={{ fontSize: "24px", color: "white" }} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <Text strong style={{ fontSize: "16px", color: "#ffffff", display: "block" }}>
                        Email Support
                      </Text>
                      <Text style={{ color: "#bbb", fontSize: "14px", marginTop: "5px" }}>
                        Send detailed inquiries via email
                      </Text>
                      <Text strong style={{ color: "#722ed1", fontSize: "16px", marginTop: "10px", display: "block" }}>
                        mwauraj118@gmail.com
                      </Text>
                    </div>
                  </div>

                  {/* Working Hours */}
                  <div style={{ 
                    display: "flex", 
                    alignItems: "center", 
                    gap: "15px",
                    padding: "20px",
                    backgroundColor: "#0a0a0a",
                    borderRadius: "15px",
                    border: "1px solid #333",
                  }}>
                    <div style={{
                      background: "linear-gradient(135deg, #fa8c16, #d46b08)",
                      borderRadius: "12px",
                      width: "56px",
                      height: "56px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}>
                      <ClockCircleOutlined style={{ fontSize: "24px", color: "white" }} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <Text strong style={{ fontSize: "16px", color: "#ffffff", display: "block" }}>
                        Always Open
                      </Text>
                      <Text style={{ color: "#52c41a", fontSize: "14px", marginTop: "5px", fontWeight: 600 }}>
                        24 Hours, 7 Days a Week
                      </Text>
                      <Text style={{ color: "#bbb", fontSize: "14px", marginTop: "10px" }}>
                        Monday - Sunday: 24/7 Customer Support
                      </Text>
                    </div>
                  </div>
                </Space>
              </Card>
            </Col>

            {/* Map Section */}
            <Col xs={24}>
              <Card
                bordered={false}
                style={{
                  borderRadius: "20px",
                  background: "linear-gradient(135deg, #1a1a1a, #222)",
                  border: "1px solid #333",
                  overflow: "hidden",
                  padding: 0,
                }}
              >
                <div style={{ padding: "20px 20px 0 20px" }}>
                  <Title level={4} style={{ color: "#ffffff", margin: 0 }}>
                    <EnvironmentOutlined style={{ marginRight: "10px", color: "#fa8c16" }} />
                    Visit Our Showroom
                  </Title>
                  <Paragraph style={{ color: "#bbb", margin: "10px 0" }}>
                    Kamakis Bypass, Ruiru - Nairobi, Kenya
                  </Paragraph>
                </div>
                <div style={{ height: "300px", width: "100%" }}>
                  <iframe 
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d127647.6294043391!2d36.8371412625!3d-1.1686430000000068!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x182f417b84b77a15%3A0x46fb9c4e87d71ab6!2sDollar%20Furniture!5e0!3m2!1sen!2ske!4v1768072545603!5m2!1sen!2ske"
                    width="100%" 
                    height="100%" 
                    style={{ border: 0 }}
                    allowFullScreen 
                    loading="lazy" 
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Dollar Furniture Location Map"
                  />
                </div>
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>
    </div>
  );
};

export default Contact;